import { useState, useEffect, useCallback } from 'react'
import { useApolloClient } from '@apollo/client'
import { Subscription, SubscriptionBooking, SubscriptionStatus } from '@customTypes/subscription.interface'
import { useAuthenticatedQuery } from './useAuth'
import { GET_USER_SUBSCRIPTIONS } from '@graphql/queries/getUserSubscriptions'

const DEFAULT_LAST_BOOKING = {
    created_at_iso_str: '',
    lot_name: '',
    lot_address: '',
    payment_method: '',
    booking_ref: '',
    price: 0,
    plates: '',
    email: ''
}

const getSubscriptionStatus = (startDate: string, endDate: string): SubscriptionStatus => {
    const now = new Date()
    const parsedStartDate = new Date(startDate)
    const parsedEndDate = new Date(endDate)

    // active
    if (now >= parsedStartDate && now <= parsedEndDate) return SubscriptionStatus.ACTIVE
    // pending
    if (now < parsedStartDate) return SubscriptionStatus.FUTURE
    // expired
    if (now > parsedEndDate) return SubscriptionStatus.CANCELLED
    return SubscriptionStatus.CANCELLED
}

const useSubscriptions = () => {
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
    const client = useApolloClient()

    const { data: subscriptionsFromApi, loading } = useAuthenticatedQuery<{
        get_user_subscriptions: Subscription[]
    }>({
        query: GET_USER_SUBSCRIPTIONS,
        fetchPolicy: 'no-cache'
    })

    const splitAddress = useCallback((address: string): { lot_address: string; lot_city: string } => {
        const addressArray = address.split(', ')
        return { lot_address: addressArray[0], lot_city: addressArray[1] }
    }, [])

    useEffect(() => {
        const parseSubscriptions = async () => {
            if (!subscriptionsFromApi) return

            const sortedSubs = subscriptionsFromApi.get_user_subscriptions.sort((a, b) =>
                a.start_date.localeCompare(b.start_date)
            )

            const parsedSubs = sortedSubs.map((subscription: Subscription) => {
                const lastBooking: SubscriptionBooking =
                    subscription.subscription_bookings[subscription.subscription_bookings.length - 1]

                const booking = lastBooking || { ...DEFAULT_LAST_BOOKING }

                return {
                    ...subscription,
                    created_at: booking.created_at_iso_str,
                    ...splitAddress(subscription.lot_address),
                    status: getSubscriptionStatus(subscription.start_date, subscription.end_date),
                    payment_method: booking.payment_method

                    // any additional fields or changes go here
                }
            })

            setSubscriptions(parsedSubs)
        }
        if (subscriptionsFromApi) {
            parseSubscriptions()
        } else {
            // error
        }
    }, [subscriptionsFromApi, client])

    const filterSubscriptionsByStatus = (status: SubscriptionStatus) => {
        return subscriptions.filter((sub) => sub.subscription_status === status)
    }

    const groupSubscriptionsByStatus = () => {
        const activeSubscriptions = filterSubscriptionsByStatus(SubscriptionStatus.ACTIVE)
        const futureSubscriptions = filterSubscriptionsByStatus(SubscriptionStatus.FUTURE)
        const completedSubscriptions = filterSubscriptionsByStatus(SubscriptionStatus.CANCELLED)

        return { activeSubscriptions, futureSubscriptions, completedSubscriptions }
    }

    const removeSubscription = (id: string) => {
        setSubscriptions(
            subscriptions.map((sub) => (sub.id === id ? { ...sub, status: SubscriptionStatus.CANCELLED } : sub))
        )
    }

    return { subscriptions, loading, filterSubscriptionsByStatus, groupSubscriptionsByStatus, removeSubscription }
}

export default useSubscriptions
